import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Users,
  RefreshCw,
  Search,
  Gift,
  Mail,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Settings,
  Eye,
  Ticket,
  MailOpen,
  UserCheck,
  UserX,
  Play,
  Send,
} from "lucide-react";


export default function CustomerReactivationPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("candidates");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Candidates
  const [candidates, setCandidates] = useState([]);
  const [candidatesTotal, setCandidatesTotal] = useState(0);
  const [candidatesPage, setCandidatesPage] = useState(0);
  const [candidateStatus, setCandidateStatus] = useState("all");
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidatePageInput, setCandidatePageInput] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  
  // Vouchers
  const [vouchers, setVouchers] = useState([]);
  const [vouchersTotal, setVouchersTotal] = useState(0);
  const [vouchersPage, setVouchersPage] = useState(0);
  const [voucherStatus, setVoucherStatus] = useState("all");
  const [voucherSearch, setVoucherSearch] = useState("");
  const [voucherPageInput, setVoucherPageInput] = useState("");
  
  // Emails
  const [emails, setEmails] = useState([]);
  const [emailsTotal, setEmailsTotal] = useState(0);
  const [emailsPage, setEmailsPage] = useState(0);
  
  // Settings
  const [settings, setSettings] = useState(null);
  
  // Dialogs
  const [redeemDialog, setRedeemDialog] = useState({ open: false, voucher: null });
  const [redeemData, setRedeemData] = useState({ pf_number: "", notes: "" });
  const [emailPreviewDialog, setEmailPreviewDialog] = useState({ open: false, type: null });
  
  const limit = 20;

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reactivation/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: candidatesPage * limit,
        limit,
      });
      if (candidateStatus !== "all") {
        params.append("status", candidateStatus);
      }
      if (candidateSearch.trim()) {
        params.append("search", candidateSearch.trim());
      }
      
      const response = await axios.get(`${API_URL}/reactivation/candidates?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCandidates(response.data.candidates);
      setCandidatesTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [token, candidatesPage, candidateStatus, candidateSearch]);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: vouchersPage * limit,
        limit,
      });
      if (voucherStatus !== "all") {
        params.append("status", voucherStatus);
      }
      if (voucherSearch.trim()) {
        params.append("search", voucherSearch.trim());
      }
      
      const response = await axios.get(`${API_URL}/reactivation/vouchers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVouchers(response.data.vouchers);
      setVouchersTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching vouchers:", error);
      toast.error("Failed to load vouchers");
    } finally {
      setLoading(false);
    }
  }, [token, vouchersPage, voucherStatus, voucherSearch]);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: emailsPage * limit,
        limit,
      });
      
      const response = await axios.get(`${API_URL}/reactivation/emails?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(response.data.emails);
      setEmailsTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching emails:", error);
      toast.error("Failed to load email log");
    } finally {
      setLoading(false);
    }
  }, [token, emailsPage]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/reactivation/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "candidates") {
      fetchCandidates();
    } else if (activeTab === "vouchers") {
      fetchVouchers();
    } else if (activeTab === "emails") {
      fetchEmails();
    } else if (activeTab === "settings") {
      fetchSettings();
    }
  }, [activeTab, fetchCandidates, fetchVouchers, fetchEmails, fetchSettings]);

  const handleScan = async () => {
    setScanning(true);
    try {
      await axios.get(`${API_URL}/reactivation/candidates/scan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Scan started in background");
      
      // Refresh after a delay
      setTimeout(() => {
        fetchCandidates();
        fetchStats();
        setScanning(false);
      }, 5000);
    } catch (error) {
      toast.error("Failed to start scan");
      setScanning(false);
    }
  };

  const handleRedeemVoucher = async () => {
    if (!redeemDialog.voucher) return;
    
    try {
      await axios.post(
        `${API_URL}/reactivation/vouchers/${redeemDialog.voucher.voucher_code}/redeem`,
        redeemData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Voucher redeemed successfully");
      setRedeemDialog({ open: false, voucher: null });
      setRedeemData({ pf_number: "", notes: "" });
      fetchVouchers();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem voucher");
    }
  };

  const handleRevokeVoucher = async (voucherCode) => {
    if (!window.confirm("Are you sure you want to revoke this voucher?")) return;
    
    try {
      await axios.post(
        `${API_URL}/reactivation/vouchers/${voucherCode}/revoke`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Voucher revoked");
      fetchVouchers();
      fetchStats();
    } catch (error) {
      toast.error("Failed to revoke voucher");
    }
  };

  const handleUpdateSettings = async (newSettings) => {
    try {
      await axios.put(
        `${API_URL}/reactivation/settings`,
        newSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(newSettings);
      toast.success("Settings updated");
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-500/20 text-yellow-600", icon: Clock },
      email_sent: { color: "bg-blue-500/20 text-blue-600", icon: Mail },
      reminder_sent: { color: "bg-purple-500/20 text-purple-600", icon: MailOpen },
      reactivated: { color: "bg-green-500/20 text-green-600", icon: UserCheck },
      active: { color: "bg-emerald-500/20 text-emerald-600", icon: CheckCircle },
    };
    
    const config = statusConfig[status] || { color: "bg-gray-500/20 text-gray-600", icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status?.replace("_", " ")}
      </Badge>
    );
  };

  const getVoucherStatusBadge = (status) => {
    const statusConfig = {
      active: { color: "bg-green-500/20 text-green-600", icon: Gift },
      redeemed: { color: "bg-blue-500/20 text-blue-600", icon: CheckCircle },
      expired: { color: "bg-gray-500/20 text-gray-600", icon: Clock },
      revoked: { color: "bg-red-500/20 text-red-600", icon: XCircle },
    };
    
    const config = statusConfig[status] || { color: "bg-gray-500/20 text-gray-600", icon: AlertCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Start Manual Email Flow - send batch of reactivation emails
  const handleStartEmailFlow = async (stage = "checkin") => {
    setSendingBatch(true);
    try {
      const response = await axios.post(
        `${API_URL}/reactivation/send-batch?batch_size=10&stage=${stage}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Email flow started: ${response.data.message}`);
      fetchCandidates();
      fetchStats();
    } catch (error) {
      console.error("Error starting email flow:", error);
      toast.error(error.response?.data?.detail || "Failed to start email flow");
    } finally {
      setSendingBatch(false);
    }
  };

  // Send email to specific candidates
  const handleSendToSelected = async () => {
    if (selectedCandidates.length === 0) {
      toast.warning("Please select candidates to send emails to");
      return;
    }
    setSendingBatch(true);
    try {
      // Send to each selected candidate
      for (const email of selectedCandidates) {
        await axios.post(
          `${API_URL}/reactivation/send-checkin`,
          { customer_email: email },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success(`Sent emails to ${selectedCandidates.length} candidates`);
      setSelectedCandidates([]);
      fetchCandidates();
    } catch (error) {
      console.error("Error sending emails:", error);
      toast.error(error.response?.data?.detail || "Failed to send emails");
    } finally {
      setSendingBatch(false);
    }
  };

  // Email Template Preview Content
  const getEmailPreview = (type) => {
    if (type === "checkin") {
      return {
        subject: "Checking if you have any upcoming shipments",
        body: `Dear [Customer Name],

I hope you are doing well.

We noticed that we have not received a transport request from your side recently, so I just wanted to check whether you might have any upcoming shipments where we could assist.

If you have any transport requirements coming up, please feel free to send the details and we will be happy to prepare a quotation.

Best regards,
Rok Kobal
Europacific Logistika d.o.o.
info@europacific.com`
      };
    } else if (type === "voucher") {
      return {
        subject: "Transport support + customs service voucher",
        body: `Dear [Customer Name],

Thank you for being a valued customer of Europacific Logistics.

As a token of our appreciation, we'd like to offer you a special voucher:

🎫 VOUCHER CODE: [VOUCHER_CODE]
Valid for 30 days

This voucher entitles you to:
• Complimentary customs clearance service on your next shipment
• Priority support from our logistics team

To redeem, simply mention this code when requesting your next quote or booking.

Best regards,
Europacific Logistics Team`
      };
    }
    return { subject: "", body: "" };
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="container mx-auto px-4 py-6 space-y-6"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Users className="h-6 w-6 text-primary" />
              Customer Reactivation
            </h1>
            <p className="text-sm text-muted-foreground">
              Automated re-engagement and voucher management
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setEmailPreviewDialog({ open: true, type: "checkin" })}
              data-testid="preview-email-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview Emails
            </Button>
            
            <Button
              variant="secondary"
              onClick={() => handleStartEmailFlow("checkin")}
              disabled={sendingBatch}
              data-testid="start-email-flow-btn"
            >
              {sendingBatch ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Start Email Flow
                </>
              )}
            </Button>
            
            <Button
              onClick={handleScan}
              disabled={scanning}
              className="bg-primary hover:bg-primary/90"
              data-testid="scan-customers-btn"
            >
              {scanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run Scan Now
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Users className="h-4 w-4" />
                  Total Customers
                </div>
                <p className="text-2xl font-bold text-foreground">{stats.customers?.total || 0}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.customers?.active || 0} active
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <UserX className="h-4 w-4" />
                  Inactive
                </div>
                <p className="text-2xl font-bold text-orange-500">{stats.customers?.inactive || 0}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.customers?.pending_reactivation || 0} pending
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <TrendingUp className="h-4 w-4" />
                  Reactivated (30d)
                </div>
                <p className="text-2xl font-bold text-green-500">{stats.customers?.reactivated_30d || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Gift className="h-4 w-4" />
                  Active Vouchers
                </div>
                <p className="text-2xl font-bold text-primary">{stats.vouchers?.active || 0}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.vouchers?.redeemed_30d || 0} redeemed
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Mail className="h-4 w-4" />
                  Emails Sent (30d)
                </div>
                <p className="text-2xl font-bold text-foreground">{stats.emails?.sent_30d || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="candidates" data-testid="tab-candidates">
              <UserX className="h-4 w-4 mr-2" />
              Candidates
            </TabsTrigger>
            <TabsTrigger value="vouchers" data-testid="tab-vouchers">
              <Ticket className="h-4 w-4 mr-2" />
              Vouchers
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">
              <Mail className="h-4 w-4 mr-2" />
              Email Log
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Candidates Tab */}
          <TabsContent value="candidates" className="space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or company..."
                  value={candidateSearch}
                  onChange={(e) => setCandidateSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchCandidates()}
                  className="pl-10"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setCandidatesPage(0); fetchCandidates(); }}
              >
                <Search className="h-4 w-4 mr-1" />
                Search
              </Button>
              <Select value={candidateStatus} onValueChange={(v) => { setCandidateStatus(v); setCandidatesPage(0); }}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="email_sent">Email Sent</SelectItem>
                  <SelectItem value="reminder_sent">Reminder Sent</SelectItem>
                  <SelectItem value="reactivated">Reactivated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Voucher</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : candidates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No reactivation candidates found
                      </TableCell>
                    </TableRow>
                  ) : (
                    candidates.map((candidate) => (
                      <TableRow key={candidate.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{candidate.customer_name || "-"}</p>
                            <p className="text-xs text-muted-foreground">{candidate.customer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>{candidate.company_name || "-"}</TableCell>
                        <TableCell>{formatDate(candidate.last_activity_at || candidate.last_contact_at)}</TableCell>
                        <TableCell>{getStatusBadge(candidate.reengagement_status)}</TableCell>
                        <TableCell>
                          {candidate.active_voucher ? (
                            <Badge variant="outline" className="text-xs">
                              {candidate.active_voucher.voucher_code}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {candidatesPage * limit + 1}-{Math.min((candidatesPage + 1) * limit, candidatesTotal)} of {candidatesTotal}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={candidatesPage === 0}
                  onClick={() => setCandidatesPage(0)}
                  title="First page"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={candidatesPage === 0}
                  onClick={() => setCandidatesPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">Page</span>
                  <Input
                    type="number"
                    min={1}
                    max={Math.ceil(candidatesTotal / limit)}
                    value={candidatePageInput || (candidatesPage + 1)}
                    onChange={(e) => setCandidatePageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const page = parseInt(candidatePageInput) - 1;
                        if (page >= 0 && page < Math.ceil(candidatesTotal / limit)) {
                          setCandidatesPage(page);
                        }
                        setCandidatePageInput("");
                      }
                    }}
                    onBlur={() => {
                      const page = parseInt(candidatePageInput) - 1;
                      if (page >= 0 && page < Math.ceil(candidatesTotal / limit)) {
                        setCandidatesPage(page);
                      }
                      setCandidatePageInput("");
                    }}
                    className="w-16 h-8 text-center"
                  />
                  <span className="text-sm text-muted-foreground">of {Math.ceil(candidatesTotal / limit)}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(candidatesPage + 1) * limit >= candidatesTotal}
                  onClick={() => setCandidatesPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(candidatesPage + 1) * limit >= candidatesTotal}
                  onClick={() => setCandidatesPage(Math.ceil(candidatesTotal / limit) - 1)}
                  title="Last page"
                >
                  Last
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Vouchers Tab */}
          <TabsContent value="vouchers" className="space-y-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by code, customer, or company..."
                  value={voucherSearch}
                  onChange={(e) => setVoucherSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchVouchers()}
                  className="pl-10"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => { setVouchersPage(0); fetchVouchers(); }}
              >
                <Search className="h-4 w-4 mr-1" />
                Search
              </Button>
              <Select value={voucherStatus} onValueChange={(v) => { setVoucherStatus(v); setVouchersPage(0); }}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="redeemed">Redeemed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : vouchers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No vouchers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    vouchers.map((voucher) => (
                      <TableRow key={voucher.id}>
                        <TableCell>
                          <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                            {voucher.voucher_code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{voucher.customer_name || "-"}</p>
                            <p className="text-xs text-muted-foreground">{voucher.customer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{voucher.company_name || "-"}</span>
                        </TableCell>
                        <TableCell>{formatDate(voucher.created_at)}</TableCell>
                        <TableCell>{formatDate(voucher.expires_at)}</TableCell>
                        <TableCell>{getVoucherStatusBadge(voucher.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {voucher.status === "active" && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-green-600"
                                  onClick={() => setRedeemDialog({ open: true, voucher })}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600"
                                  onClick={() => handleRevokeVoucher(voucher.voucher_code)}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {vouchersPage * limit + 1}-{Math.min((vouchersPage + 1) * limit, vouchersTotal)} of {vouchersTotal}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={vouchersPage === 0}
                  onClick={() => setVouchersPage(0)}
                  title="First page"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={vouchersPage === 0}
                  onClick={() => setVouchersPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">Page</span>
                  <Input
                    type="number"
                    min={1}
                    max={Math.ceil(vouchersTotal / limit)}
                    value={voucherPageInput || (vouchersPage + 1)}
                    onChange={(e) => setVoucherPageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const page = parseInt(voucherPageInput) - 1;
                        if (page >= 0 && page < Math.ceil(vouchersTotal / limit)) {
                          setVouchersPage(page);
                        }
                        setVoucherPageInput("");
                      }
                    }}
                    onBlur={() => {
                      const page = parseInt(voucherPageInput) - 1;
                      if (page >= 0 && page < Math.ceil(vouchersTotal / limit)) {
                        setVouchersPage(page);
                      }
                      setVoucherPageInput("");
                    }}
                    className="w-16 h-8 text-center"
                  />
                  <span className="text-sm text-muted-foreground">of {Math.ceil(vouchersTotal / limit)}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(vouchersPage + 1) * limit >= vouchersTotal}
                  onClick={() => setVouchersPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(vouchersPage + 1) * limit >= vouchersTotal}
                  onClick={() => setVouchersPage(Math.ceil(vouchersTotal / limit) - 1)}
                  title="Last page"
                >
                  Last
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Emails Tab */}
          <TabsContent value="emails" className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ) : emails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No emails sent yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    emails.map((email) => (
                      <TableRow key={email.id}>
                        <TableCell>{formatDate(email.sent_at || email.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {email.email_type?.replace(/_/g, " ")}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{email.customer_name || "-"}</p>
                            <p className="text-xs text-muted-foreground">{email.customer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{email.subject}</TableCell>
                        <TableCell>
                          {email.status === "sent" ? (
                            <Badge className="bg-green-500/20 text-green-600">Sent</Badge>
                          ) : (
                            <Badge className="bg-red-500/20 text-red-600">Failed</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {emailsPage * limit + 1}-{Math.min((emailsPage + 1) * limit, emailsTotal)} of {emailsTotal}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={emailsPage === 0}
                  onClick={() => setEmailsPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(emailsPage + 1) * limit >= emailsTotal}
                  onClick={() => setEmailsPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {settings && (
              <Card>
                <CardHeader>
                  <CardTitle>Reactivation Automation Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable Automation</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically scan and send re-engagement emails
                      </p>
                    </div>
                    <Switch
                      checked={settings.enabled}
                      onCheckedChange={(checked) => handleUpdateSettings({ ...settings, enabled: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Auto-Send Emails</Label>
                      <p className="text-sm text-muted-foreground">
                        Send emails automatically (vs manual approval)
                      </p>
                    </div>
                    <Switch
                      checked={settings.auto_send_emails}
                      onCheckedChange={(checked) => handleUpdateSettings({ ...settings, auto_send_emails: checked })}
                    />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Inactivity Threshold (days)</Label>
                      <Input
                        type="number"
                        value={settings.inactivity_days}
                        onChange={(e) => handleUpdateSettings({ ...settings, inactivity_days: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>No Email Within (days)</Label>
                      <Input
                        type="number"
                        value={settings.no_email_days}
                        onChange={(e) => handleUpdateSettings({ ...settings, no_email_days: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Reminder After (days)</Label>
                      <Input
                        type="number"
                        value={settings.reminder_days}
                        onChange={(e) => handleUpdateSettings({ ...settings, reminder_days: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Voucher Validity (days)</Label>
                      <Input
                        type="number"
                        value={settings.voucher_validity_days}
                        onChange={(e) => handleUpdateSettings({ ...settings, voucher_validity_days: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="bg-muted/50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Automation Flow</h4>
                    <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Daily scan at 8:00 AM CET identifies inactive customers (no activity in {settings.inactivity_days} days)</li>
                      <li>Customers with no offer/quote in {settings.inactivity_days}+ days are tagged as "Reactivation Candidate"</li>
                      <li>System sends re-engagement email with {settings.voucher_validity_days}-day voucher (max once per {settings.no_email_days} days)</li>
                      <li>If no response, a softer reminder is sent after {settings.reminder_days} days</li>
                      <li>When customer responds or redeems voucher, they are marked "Active"</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Redeem Voucher Dialog */}
      <Dialog open={redeemDialog.open} onOpenChange={(open) => setRedeemDialog({ open, voucher: open ? redeemDialog.voucher : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redeem Voucher</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="font-mono text-lg">{redeemDialog.voucher?.voucher_code}</p>
              <p className="text-sm text-muted-foreground">{redeemDialog.voucher?.customer_name}</p>
              <p className="text-xs text-muted-foreground">{redeemDialog.voucher?.description}</p>
            </div>
            <div className="space-y-2">
              <Label>PF Number (Shipment Reference)</Label>
              <Input
                value={redeemData.pf_number}
                onChange={(e) => setRedeemData({ ...redeemData, pf_number: e.target.value })}
                placeholder="e.g., PF-000123-26"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={redeemData.notes}
                onChange={(e) => setRedeemData({ ...redeemData, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemDialog({ open: false, voucher: null })}>
              Cancel
            </Button>
            <Button onClick={handleRedeemVoucher}>
              Redeem Voucher
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Preview Dialog */}
      <Dialog open={emailPreviewDialog.open} onOpenChange={(open) => setEmailPreviewDialog({ open, type: open ? emailPreviewDialog.type : null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Templates Preview</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="checkin" className="mt-4">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="checkin">Check-in Email</TabsTrigger>
              <TabsTrigger value="voucher">Voucher Email</TabsTrigger>
            </TabsList>
            <TabsContent value="checkin" className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Subject:</Label>
                <div className="p-3 bg-muted rounded-lg text-sm">
                  {getEmailPreview("checkin").subject}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Body:</Label>
                <div className="p-4 bg-muted rounded-lg text-sm whitespace-pre-line max-h-64 overflow-y-auto">
                  {getEmailPreview("checkin").body}
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleStartEmailFlow("checkin")} disabled={sendingBatch}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Check-in Batch (10)
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="voucher" className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Subject:</Label>
                <div className="p-3 bg-muted rounded-lg text-sm">
                  {getEmailPreview("voucher").subject}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Body:</Label>
                <div className="p-4 bg-muted rounded-lg text-sm whitespace-pre-line max-h-64 overflow-y-auto">
                  {getEmailPreview("voucher").body}
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleStartEmailFlow("voucher")} disabled={sendingBatch}>
                  <Send className="h-4 w-4 mr-2" />
                  Send Voucher Batch (10)
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
